/**
 * Copying one aircraft's MAVLink to another ground station.
 *
 * Mission Planner or QGC on a laptop can watch the same aircraft by being sent
 * every frame it transmits, unchanged, over UDP. Nothing that station sends
 * comes back, so it watches and cannot command.
 *
 * Anyone who can see the aircraft can see where its telemetry is going; only
 * someone who controls it can change that. The server is the check for both,
 * and for the targets it refuses: this server itself, more than four, and a
 * name that does not resolve. Its reason is shown as it is written.
 */

import { useEffect, useState, type ReactNode } from 'react';

import {
  addForwarding,
  getForwarding,
  removeForwarding,
  type Forwarding,
} from '@/api/connections';
import { useCanControl } from '@/stores/authStore';
import { useDroneIdentity } from '@/stores/droneStore';
import type { DroneId } from '@/types/domain';

const DANGER = 'oklch(0.72 0.19 28)';
const MUTED = 'rgba(244,244,245,0.5)';

/** Mirrors MAX_TARGETS_PER_AIRCRAFT in api/app/domain/forwarding.py. */
const MAX_TARGETS = 4;
const DEFAULT_GCS_PORT = 14550;

export function ForwardingPanel({ droneId }: { droneId: DroneId }): ReactNode {
  const canControl = useCanControl(droneId);
  const identity = useDroneIdentity(droneId);
  const [forwarding, setForwarding] = useState<Forwarding | null>(null);
  const [host, setHost] = useState('');
  const [port, setPort] = useState(String(DEFAULT_GCS_PORT));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setForwarding(null);
    setError(null);
    void getForwarding(droneId).then((result) => {
      if (!live) return;
      if (result.ok) setForwarding(result.data);
      else setError(result.error.message);
    });
    return () => {
      live = false;
    };
  }, [droneId]);

  if (identity?.simulated) {
    return (
      <p className="text-[11px]" style={{ color: MUTED }}>
        A simulated aircraft has no MAVLink link, so there is nothing to forward.
      </p>
    );
  }

  const targets = forwarding?.targets ?? [];
  const portNumber = Number(port);
  const portValid = Number.isInteger(portNumber) && portNumber >= 1 && portNumber <= 65535;

  async function add() {
    setBusy(true);
    setError(null);
    const result = await addForwarding(droneId, { host: host.trim(), port: portNumber });
    setBusy(false);
    if (result.ok) {
      setForwarding(result.data);
      setHost('');
    } else {
      setError(result.error.message);
    }
  }

  async function remove(target: string) {
    setError(null);
    const result = await removeForwarding(droneId, target);
    if (result.ok) setForwarding(result.data);
    else setError(result.error.message);
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[11px]" style={{ color: 'rgba(244,244,245,0.85)' }}>
        Every frame this aircraft sends is copied, unchanged, to each address below over UDP. Point
        Mission Planner or QGC at the port to watch it. Nothing those stations send comes back.
      </p>

      {targets.length === 0 ? (
        <p className="text-[11px]" style={{ color: MUTED }}>
          {forwarding ? 'Not forwarding anywhere.' : 'Reading…'}
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5" aria-label="Forwarding targets">
          {targets.map((target) => (
            <li
              key={target.target}
              className="flex items-center gap-2 rounded-[10px] border px-3 py-1.5"
              style={{ borderColor: 'rgba(255,255,255,0.13)' }}
            >
              <span className="font-mono text-[11px]" style={{ color: '#f4f4f5' }}>
                {target.target}
              </span>
              {canControl && (
                <button
                  type="button"
                  onClick={() => void remove(target.target)}
                  aria-label={`Stop forwarding to ${target.target}`}
                  className="ml-auto label-mono text-[9px]"
                  style={{ color: MUTED }}
                >
                  REMOVE
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {forwarding && forwarding.framesSent > 0 && (
        <p className="label-mono text-[9px]" style={{ color: MUTED }}>
          {forwarding.framesSent} FRAMES SENT
          {forwarding.framesFailed > 0 ? `, ${forwarding.framesFailed} FAILED` : ''}
        </p>
      )}

      {canControl ? (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="label-mono text-[9px]" style={{ color: MUTED }}>
              GROUND STATION
            </span>
            <input
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="IP address or hostname"
              aria-label="Ground station address"
              className="rounded-[8px] border bg-transparent px-2 py-1 font-mono text-[11px]"
              style={{ borderColor: 'rgba(255,255,255,0.18)', color: '#f4f4f5' }}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="label-mono text-[9px]" style={{ color: MUTED }}>
              PORT
            </span>
            <input
              value={port}
              onChange={(e) => setPort(e.target.value)}
              inputMode="numeric"
              aria-label="Ground station port"
              className="w-[80px] rounded-[8px] border bg-transparent px-2 py-1 font-mono text-[11px]"
              style={{ borderColor: 'rgba(255,255,255,0.18)', color: '#f4f4f5' }}
            />
          </label>
          <button
            type="button"
            disabled={busy || !host.trim() || !portValid || targets.length >= MAX_TARGETS}
            onClick={() => void add()}
            className="rounded-[10px] border px-4 py-1.5 label-mono text-[11px] disabled:opacity-40"
            style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(244,244,245,0.85)' }}
          >
            {busy ? 'ADDING…' : 'FORWARD'}
          </button>
        </div>
      ) : (
        <p className="text-[11px]" style={{ color: MUTED }}>
          Read-only: changing where this aircraft is forwarded needs operator role and control of
          it.
        </p>
      )}

      {targets.length >= MAX_TARGETS && canControl && (
        <p className="text-[11px]" style={{ color: MUTED }}>
          Four is the most one aircraft can forward to. Remove one to add another.
        </p>
      )}

      {error && (
        <p className="text-[11px]" style={{ color: DANGER }}>
          {error}
        </p>
      )}
    </div>
  );
}
